import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  VStack,
  Stack,
  useToast,
  Collapse,
  Icon,
  useDisclosure,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { Chart as ChartJS, CategoryScale, LinearScale, BarController, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from "chart.js";
import { Chart } from "react-chartjs-2";
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { BarChart3, BookOpen, ClipboardList, Pencil } from 'lucide-react';
import axios from '../../axiosConfig';
import { useUser } from '../../contexts/UserContext';
import OrganizationDetailHeader from './OrganizationDetailHeader';
import OrganizationInfoSection from './OrganizationInfoSection';
import OrganizationBillingSection from './OrganizationBillingSection';
import OrganizationNotesSection from './OrganizationNotesSection';
import OrganizationRelationshipsSection from './OrganizationRelationshipsSection';
import OrganizationTrialSection from './OrganizationTrialSection';
import TourModeActionButton from './TourModeActionButton';
import ContextUserCreateModal from '../users/ContextUserCreateModal';
import { canManageUsersForOrganization } from '../../utils/userCreationPermissions';
import { hasRole } from '../../utils/authorization';
import {
  activateOrganizationTrial,
  extendOrganizationTrial,
  forceExpireOrganizationTrial,
  markOrganizationAsTrial,
} from './organizationTrialApi';
import { fetchWorkbookAvailability } from './organizationSurveysApi';
import { formatOrganizationSurveyTitle } from '../../utils/organizationSurveyHelper';

ChartJS.register(CategoryScale, LinearScale, BarElement, BarController, LineElement, PointElement, Title, Tooltip, Legend);

const hasSurveyResultData = (surveys) => (
  Array.isArray(surveys) &&
  surveys.some((survey) => (
    Array.isArray(survey?.pages)
      ? survey.pages.length > 0
      : Boolean(survey?.survey_id || survey?.surveyId || survey?.id)
  ))
);

const OrganizationView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const createUserModal = useDisclosure();
  const trialActionDialog = useDisclosure();
  const { user: currentUser } = useUser();
  const cancelTrialActionRef = useRef(null);
  const [organization, setOrganization] = useState(null);
  const [stats, setStats] = useState({ totalRespondents: 0, completedSurveys: 0 });
  const [surveyResults, setSurveyResults] = useState(null);
  const [primaryOrganizationUser, setPrimaryOrganizationUser] = useState(null);
  const [showCharts, setShowCharts] = useState(false);
  const [isMutatingTrial, setIsMutatingTrial] = useState(false);
  const [pendingTrialAction, setPendingTrialAction] = useState(null);
  const [hasWorkbook, setHasWorkbook] = useState(false);
  const [hasResults, setHasResults] = useState(false);

  const loadOrganizationView = useCallback(async () => {
    try {
      const [orgRes, statsRes, surveyRes, userRes, workbookAvailable] = await Promise.all([
        axios.get(`/organizations/${id}`),
        axios.get(`/organizations/${id}/stats`),
        axios.get(`/organizations/${id}/survey_results_by_page`),
        axios.get(`/organizations/${id}/user`).catch(e => e.response?.status === 404 ? null : Promise.reject(e)),
        fetchWorkbookAvailability(id).catch(() => false),
      ]);
      setOrganization(orgRes.data);
      setStats(statsRes.data);
      const nextSurveyResults = surveyRes.data.surveys;
      setSurveyResults(nextSurveyResults);
      setPrimaryOrganizationUser(userRes?.data || null);
      setHasWorkbook(workbookAvailable);
      setHasResults(workbookAvailable || hasSurveyResultData(nextSurveyResults));
    } catch (error) {
      console.error('Error loading organization view:', error);
      toast({ title: 'Could not load school', description: 'Refresh the page and try again.', status: 'error', duration: 3000, isClosable: true });
      navigate('/app/organizations');
    }
  }, [id, navigate, toast]);

  useEffect(() => {
    loadOrganizationView();
  }, [loadOrganizationView]);

  const renderCharts = () => {
    if (!surveyResults) return <Text>Loading charts...</Text>;

    return surveyResults.map((survey, index) => {
      const labels = survey.pages.map(p => p.page_title);
      const scoreDistribution = {};
      survey.pages.forEach((page, idx) => {
        Object.entries(page.distribution).forEach(([score, count]) => {
          scoreDistribution[score] = scoreDistribution[score] || Array(labels.length).fill(0);
          scoreDistribution[score][idx] = count;
        });
      });
      const tableauColors = [
        "#4E79A7", // blue
        "#F28E2B", // orange
        "#E15759", // red
        "#76B7B2", // teal
        "#59A14F", // green
        "#EDC948", // yellow
        "#B07AA1", // purple
        "#FF9DA7", // pink
        "#9C755F", // brown
        "#BAB0AC"  // gray
      ];
      const barDatasets = Object.entries(scoreDistribution).map(([score, data], i) => ({
        label: `Score ${score}`,
        data,
        backgroundColor: tableauColors[i % tableauColors.length],
        yAxisID: "y",
        order: 2,
      }));
      const lineDataset = {
        label: "Average Score",
        data: survey.pages.map(p => p.average_score),
        type: "line",
        borderColor: "#0000FF", // "#0ea5e9",
        borderWidth: 2,
        pointBackgroundColor: "#0000FF", // "#0ea5e9",
        tension: 0.3,
        yAxisID: "y2",
        order: 1,
      };
      return (
        <Box key={index} bg="white" p={6} shadow="md" borderRadius="md" mt={4} mb={6} width="100%" height="400px">
          <Text fontWeight="bold" mb={4}>{formatOrganizationSurveyTitle(survey)}</Text>
          <Chart type="bar" data={{ labels, datasets: [...barDatasets, lineDataset] }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: true, position: "top" }, tooltip: { mode: "index", intersect: false } }, scales: { x: { stacked: true, title: { display: true, text: "Survey Pages" } }, y: { stacked: true, title: { display: true, text: "Response Count" } }, y2: { title: { display: true, text: "Average Score (1-7)" }, position: "right", min: 1, max: 7, grid: { drawOnChartArea: false } } } }} />
        </Box>
      );
    });
  };

  if (!organization) return <Text>Loading school details...</Text>;

  const organizationUsers = Array.isArray(organization?.users) && organization.users.length > 0
    ? organization.users
    : (primaryOrganizationUser ? [primaryOrganizationUser] : []);
  const organizationNetworks = Array.isArray(organization?.networks) ? organization.networks : [];
  const isTour = organization?.tour_mode === true;
  const canManageUsers = canManageUsersForOrganization(currentUser, organization?.id);
  const canManageTrial = hasRole(currentUser, 'hke_admin');

  const handleTrialMutation = async (action, successTitle, errorTitle) => {
    setIsMutatingTrial(true);
    try {
      await action();
      toast({ title: successTitle, status: 'success', duration: 3000, isClosable: true });
      await loadOrganizationView();
    } catch (error) {
      console.error(errorTitle, error);
      toast({ title: errorTitle, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setIsMutatingTrial(false);
    }
  };

  const handleMarkTrial = async (days) => handleTrialMutation(
    () => markOrganizationAsTrial(id, { days }),
    'School marked as trial',
    'Could not mark school as trial'
  );

  const handleExtendTrial = async (days) => handleTrialMutation(
    () => extendOrganizationTrial(id, { days }),
    'Trial extended',
    'Could not extend trial'
  );

  const openTrialActionDialog = (action) => {
    setPendingTrialAction(action);
    trialActionDialog.onOpen();
  };

  const closeTrialActionDialog = () => {
    if (isMutatingTrial) return;
    setPendingTrialAction(null);
    trialActionDialog.onClose();
  };

  const handleForceExpireTrial = () => {
    openTrialActionDialog('force_expire');
  };

  const handleActivateTrial = () => {
    openTrialActionDialog('activate_permanent');
  };

  const handleConfirmTrialAction = async () => {
    if (pendingTrialAction === 'force_expire') {
      await handleTrialMutation(
        () => forceExpireOrganizationTrial(id),
        'Trial expired',
        'Could not force expire trial'
      );
    }

    if (pendingTrialAction === 'activate_permanent') {
      await handleTrialMutation(
        () => activateOrganizationTrial(id),
        'Permanent access activated',
        'Could not activate permanent access'
      );
    }

    closeTrialActionDialog();
  };

  const trialActionDialogCopy = pendingTrialAction === 'force_expire'
    ? {
        title: 'Force expire trial',
        body: 'Are you sure you want to expire this school trial now?',
        confirmLabel: 'Force expire',
        confirmColorScheme: 'red',
      }
    : {
        title: 'Activate permanent access',
        body: 'Are you sure you want to activate permanent access for this school?',
        confirmLabel: 'Activate permanently',
        confirmColorScheme: 'green',
      };

  return (
    <Box p={6} maxW="6xl" mx="auto">
      <OrganizationDetailHeader
        title={organization.name}
        subtitle="School details"
        actions={[
          {
            label: 'Edit school',
            to: `/app/organizations/${id}/edit`,
            leftIcon: <Icon as={Pencil} boxSize={4} />,
            variant: 'outline',
            colorScheme: 'yellow',
            size: 'md',
            isTour,
          },
          {
            label: 'Manage Surveys',
            to: `/app/organizations/${id}/surveys`,
            leftIcon: <Icon as={ClipboardList} boxSize={4} />,
            variant: 'outline',
            colorScheme: 'blue',
            size: 'md',
          },
          ...(hasResults ? [{
            label: 'Survey Results',
            to: `/app/organizations/${id}/results`,
            leftIcon: <Icon as={BarChart3} boxSize={4} />,
            variant: 'outline',
            colorScheme: 'green',
            size: 'md',
          }] : []),
          ...(hasWorkbook ? [{
            label: 'View Workbook',
            to: `/app/organizations/${id}/workbook`,
            leftIcon: <Icon as={BookOpen} boxSize={4} />,
            variant: 'outline',
            colorScheme: 'purple',
            size: 'md',
          }] : []),
        ]}
      />

      <Stack spacing={4}>
        <OrganizationInfoSection organization={organization} />
        <OrganizationTrialSection
          organization={organization}
          canManageTrial={canManageTrial}
          isTour={isTour}
          isMutatingTrial={isMutatingTrial}
          onMarkTrial={handleMarkTrial}
          onExtendTrial={handleExtendTrial}
          onForceExpire={handleForceExpireTrial}
          onActivatePermanently={handleActivateTrial}
        />
        <OrganizationBillingSection organization={organization} />
        <OrganizationNotesSection notes={organization.notes} />
        <OrganizationRelationshipsSection
          users={organizationUsers}
          networks={organizationNetworks}
          userActionLabel={canManageUsers ? 'Add User' : null}
          onUserAction={canManageUsers ? createUserModal.onOpen : null}
          isTour={isTour}
        />
      </Stack>

      <Card mt={6} mb={6}><CardHeader><Heading size="md">School Stats</Heading></CardHeader><CardBody>
        <VStack spacing={2} align="start">
          <Text><strong>Total Respondents:</strong> {stats.totalRespondents}</Text>
          <Text><strong>Completed Surveys:</strong> {stats.completedSurveys}</Text>
        </VStack>
      </CardBody></Card>

      <Button mb={4} onClick={() => setShowCharts((prev) => !prev)} leftIcon={showCharts ? <FaChevronUp /> : <FaChevronDown />} variant="outline" colorScheme="blue" fontWeight="normal">
        {showCharts ? 'Hide Survey Charts' : 'Show Survey Charts'}
      </Button>
      <Collapse in={showCharts} animateOpacity>{renderCharts()}</Collapse>

      <ContextUserCreateModal
        isOpen={createUserModal.isOpen}
        onClose={createUserModal.onClose}
        context={{ type: 'organization', id: Number(id) }}
        title="Create school user"
        submitLabel="Create user"
        onCreated={loadOrganizationView}
      />

      <AlertDialog
        isOpen={trialActionDialog.isOpen}
        leastDestructiveRef={cancelTrialActionRef}
        onClose={closeTrialActionDialog}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>{trialActionDialogCopy.title}</AlertDialogHeader>
            <AlertDialogBody>{trialActionDialogCopy.body}</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelTrialActionRef} onClick={closeTrialActionDialog} isDisabled={isMutatingTrial}>
                Cancel
              </Button>
              <TourModeActionButton
                colorScheme={trialActionDialogCopy.confirmColorScheme}
                ml={3}
                onClick={handleConfirmTrialAction}
                isTour={isTour}
                isLoading={isMutatingTrial}
              >
                {trialActionDialogCopy.confirmLabel}
              </TourModeActionButton>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default OrganizationView;
